import { useEffect, useRef, useState, type Ref } from "react";
import { ArrowRight } from "lucide-react";
import { CourseMarkdown } from "@/components/storyboard/CourseMarkdown";
import { objectivesListMapping } from "@/components/course/ObjectivesListMapping";
import KickerBadge from "@/components/course/KickerBadge";
import PageHeadline from "@/components/course/PageHeadline";
import { Button } from "@/components/ui/button";
import { contentEnterClass } from "@/lib/contentEnterAnimation";
import { cn } from "@/lib/utils";

interface SectionContentProps {
  /** Small pill label above the title, e.g. "Modul 1 von 1". */
  badge?: string;
  title: string;
  body: string;
  ctaLabel: string;
  /** When omitted, no call-to-action button is shown. */
  onStart?: () => void;
  /** Optional ref on the page heading — used to move focus after outro unveil. */
  headingRef?: Ref<HTMLHeadingElement | null>;
  className?: string;
}

export const SectionContent = ({
  badge,
  title,
  body,
  ctaLabel,
  onStart,
  headingRef,
  className,
}: SectionContentProps) => {
  const timeoutRef = useRef<number | null>(null);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    return () => {
      if (timeoutRef.current != null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleStart = () => {
    if (!onStart || isExiting) return;

    setIsExiting(true);
    timeoutRef.current = window.setTimeout(() => {
      onStart();
    }, 260);
  };

  return (
    <div
      className={cn(
        contentEnterClass,
        "transition-all duration-300 ease-out motion-reduce:transform-none motion-reduce:transition-none",
        isExiting && "translate-y-3 opacity-0",
        className
      )}
    >
      <div className="max-w-3xl pb-[calc(1.5rem+env(safe-area-inset-bottom))] sm:pb-6">
        {badge && <KickerBadge className="mb-4">{badge}</KickerBadge>}

        <PageHeadline
          title={title}
          level="h1"
          headingRef={headingRef}
          className="mb-8"
        />

        <div className="prose max-w-full text-h4 leading-body marker:text-primary prose-strong:text-primary">
          <CourseMarkdown components={objectivesListMapping}>
            {body}
          </CourseMarkdown>
        </div>

        {onStart && (
          <div className="mt-8">
            <Button
              size="xl"
              onClick={handleStart}
              disabled={isExiting}
              className="w-full sm:w-auto"
            >
              {ctaLabel}
              <ArrowRight />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
